import { Grid, Tab, Typography } from "@mui/material";
import { format } from "date-fns";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { fetchEmploymentAttributesOverview } from "../employment/employmentSlice";
import { fetchTalentTags } from "../talentTags/talentTagsSlice";
import { fetchTalent, selectTalentById } from "../talents/talentsSlice";
import { clearWorkHistories } from "../workHistory/WorkHistoriesSlice";
import TalentEmptyState from "./TalentEmptyState";
import TalentProfileTabPanels from "./TalentProfileTabPanels";
import { StyledTabs } from "./styles";
import TalentProfileHeader from "./talentProfileHeader/TalentProfileHeader";
import TalentTags from "./talentTags/TalentTags";

export const TalentProfile = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");
  const { talentId } = useParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const navigate = useNavigate();

  const tabNames = useMemo(
    () => [
      "profile",
      "jobApplications",
      "employment",
      "entries",
      "calendar",
      "poolpro",
      "selfSignUp",
    ],
    [],
  );
  const params = useParams();

  useEffect(() => {
    if (params.tab) {
      const tabIndex = tabNames.indexOf(params.tab);
      // "blacklist" was the old URL segment before the tab was renamed to "poolpro"
      setTabIndex(
        tabIndex !== -1 ? tabIndex : params.tab === "blacklist" ? 5 : -1,
      );
    }
  }, [params, tabNames]);

  useEffect(() => {
    // Fetch talents tags to have names for ids
    dispatch(fetchTalentTags({}));
  }, [dispatch]);

  // TODO: consider isolating cleanup logic if more data needs to be wiped
  useEffect(() => {
    return () => {
      dispatch(clearWorkHistories());
    };
  }, [dispatch]);

  const handleTabChange = (_: SyntheticEvent, newTabIndex: number) => {
    navigate(`/home/talents/${talentId}/${tabNames[newTabIndex]}`);
    setTabIndex(newTabIndex);
  };

  const talent = useAppSelector((state) => selectTalentById(state, talentId!!));

  const tabs = [
    { label: t("profile"), value: 0 },
    { label: t("applications"), value: 1 },
    { label: t("employmentTab"), value: 2 },
    { label: t("worktimeEntries"), value: 3 },
    { label: t("calendar.tab"), value: 4 },
    { label: t("blacklistAndPoolPro"), value: 5 },
    { label: t("shiftSelfSignUp"), value: 6 },
  ];

  useEffect(() => {
    if (talentId) {
      const date = format(new Date(), "yyyy-MM-dd");
      dispatch(fetchEmploymentAttributesOverview({ talentId, date }));

      dispatch(fetchTalent({ talentId })).then(() =>
        setIsInitialLoading(false),
      );
    }
  }, [talentId, dispatch, tabIndex]);

  return talent ? (
    <>
      <Outlet />
      <Grid xs={12}>
        <TalentProfileHeader talent={talent} />
        <TalentTags tags={talent?.account?.tags || []} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StyledTabs value={tabIndex} onChange={handleTabChange}>
              {tabs.map((tab) => (
                <Tab
                  value={tab.value}
                  to={`/home/talents/${talentId}/${tabNames[tab.value]}`}
                  component={Link}
                  key={"talentProfile-tab-" + tab.value}
                  label={
                    <Typography variant="header" color="textSecondary">
                      {tab.label}
                    </Typography>
                  }
                />
              ))}
            </StyledTabs>
          </Grid>
          <TalentProfileTabPanels tabIndex={tabIndex} talent={talent} />
        </Grid>
      </Grid>
    </>
  ) : (
    <TalentEmptyState isLoading={isInitialLoading} />
  );
};
